"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowLeft, BookOpen, Calendar, Clock, RefreshCw } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function stripHtml(html?: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>?/gm, "").trim();
}
function formatDate(dateString?: string): string {
  if (!dateString) return "";
  try {
    const d = new Date(dateString);
    return new Intl.DateTimeFormat("ar-SA", {
      year: "numeric",
      month: "long",
      day: "numeric"
    }).format(d);
  } catch {
    return dateString;
  }
}
function PostsGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    isRefetching
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xs">
            <div className="h-48 w-full animate-pulse bg-muted" />
            <div className="flex flex-1 flex-col gap-3 p-6">
              <div className="h-4 w-28 animate-pulse rounded bg-muted" />
              <div className="h-6 w-4/5 animate-pulse rounded bg-muted" />
              <div className="h-4 w-full animate-pulse rounded bg-muted" />
              <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
              <div className="mt-auto pt-4">
                <div className="h-9 w-32 animate-pulse rounded bg-muted" />
              </div>
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card p-12 text-center shadow-2xs">
        <p className="text-base text-muted-foreground">
          تعذر تحميل المقالات في الوقت الحالي. يرجى إعادة المحاولة.
        </p>
        <Button onClick={() => refetch()} variant="outline" className="mt-4 gap-2 border-border">
          <RefreshCw className="size-4" />
          إعادة المحاولة
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card p-12 text-center shadow-2xs">
        <BookOpen className="size-10 text-muted-foreground/60 mb-3" />
        <p className="text-base font-medium text-foreground">
          لا توجد مقالات منشورة حالياً
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          تابعنا قريباً للمزيد من النصائح الإرشادية حول صيانة أفران الطهي.
        </p>
      </div>;
  }
  return <div className="space-y-10">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => {
        const rawExcerpt = stripHtml(post.excerpt || post.content || "");
        const truncatedExcerpt = rawExcerpt.length > 120 ? `${rawExcerpt.slice(0, 120)}...` : rawExcerpt;
        const articleUrl = post.link || `/المدونة/${post.slug || post.id}`;
        return <Card key={post.id} data-index={i} className="group flex flex-col justify-between overflow-hidden rounded-lg border border-border bg-card p-0 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-md">
              <div>
                <div className="relative aspect-video w-full overflow-hidden bg-muted">
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title || "صورة المقال"} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-102" /> : <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/oven_maintenance_guide_article.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/oven_maintenance_guide_article.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/oven_maintenance_guide_article.webp-wvc-sizes" alt={post.title || "نصائح وإرشادات صيانة الأفران"} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-102" />}
                </div>

                <CardHeader className="space-y-2 p-6 pb-2">
                  {post.date && <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <SafeHydrate><Calendar className="size-3.5 text-primary" /></SafeHydrate>
                      <time dateTime={post.date}>{formatDate(post.date)}</time>
                    </div>}
                  <CardTitle className="text-lg font-bold leading-snug text-foreground transition-colors group-hover:text-primary">
                    <Link to={articleUrl} className="hover:underline">
                      {post.title}
                    </Link>
                  </CardTitle>
                </CardHeader>

                <CardContent className="px-6 py-2">
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {truncatedExcerpt || "إرشادات عملية لفحص وصيانة أفران الغاز والكهرباء وضمان أمان التشغيل المنزلي."}
                  </p>
                </CardContent>
              </div>

              <CardFooter className="border-t border-border/80 px-6 py-4">
                <Button asChild variant="link" className="h-auto p-0 text-sm font-semibold text-primary transition-all group-hover:gap-2.5">
                  <Link to={articleUrl} className="inline-flex items-center gap-1.5">
                    <span>قراءة المقال كاملاً</span>
                    <ArrowLeft className="size-4 transition-transform group-hover:-translate-x-1" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>;
      })}
      </div>

      {(hasNext || hasPrev) && <div className="flex items-center justify-center gap-4 pt-4 border-t border-border">
          <Button onClick={() => prevPage()} disabled={!hasPrev || isRefetching} variant="outline" size="sm" className="border-border text-foreground">
            السابق
          </Button>
          <Button onClick={() => nextPage()} disabled={!hasNext || isRefetching} variant="outline" size="sm" className="border-border text-foreground">
            التالي
          </Button>
        </div>}
    </div>;
}
export default function ArticlesList() {
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="قائمة-المقالات" data-nav="light" dir="rtl" className="relative bg-background text-foreground py-16 md:py-20 lg:py-24">
      <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8">
        <header className="mb-12 max-w-3xl space-y-3">
          <div className="inline-block h-1 w-12 rounded-full bg-primary" />
          <h1 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl font-default">
            مقالات ونصائح عن صيانة وتنظيف الأفران
          </h1>
          <p className="text-base leading-relaxed text-muted-foreground md:text-lg">
            دليلك المنزلي الشامل للمحافظة على كفاءة الفرن، ترشيد استهلاك الغاز، والتعامل الآمن مع الأعطال اليومية.
          </p>
        </header>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }} className="w-full">
          <PostsGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}